import type {
  NavigationGuard,
  NavigationHookAfter,
  RouteLocationRaw,
  RouteLocationNormalized,
} from 'vue-router'
import { useAuthStore } from '@/store'
import { clearPage } from 'stepin/es/tabs-view'
import { LOGIN_ROUTE, DEFAULT_ROUTE, DEFAULT_ROUTE_PATH } from './constants'

function isPublicRoute(to: RouteLocationNormalized) {
  return to.matched.some((r) => r.meta?.public === true)
}

/**
 * 提取并校验 redirect，防止开放重定向（仅允许以 "/" 开头的站内相对路径）
 */
function getSafeRedirect(raw?: unknown): string | undefined {
  if (typeof raw !== 'string' || !raw) return
  let decoded = raw
  try {
    decoded = decodeURIComponent(raw)
  } catch {
    // ignore decode error
  }
  // 必须是站内相对路径
  if (!decoded.startsWith('/')) return
  // 拦截 http(s) 或协议相对外链
  if (/^(https?:)?\/\//i.test(decoded)) return
  return decoded
}

/**
 * 登录判断守卫：基于统一的 auth 门面
 *
 * 设计原则：
 * 1. 等待认证状态就绪（通过 auth.whenReady()）
 * 2. 只依赖单一数据源（auth store），不直接读 localStorage
 * 3. 简洁清晰，职责单一
 *
 * 前置条件：
 * - main.ts 中已调用 auth.hydrateFromStorage()
 * - 首次导航时状态已恢复完成
 */
export const loginGuard: NavigationGuard = async (to, _from, next) => {
  // 记录邀请参数但不短路，让后续登录/权限校验继续执行
  const { uuid, referrer_code } = to.query
  if (uuid && referrer_code) {
    const lastReferrerCodeObj = JSON.parse(localStorage.getItem('lastReferrerCodeObj') || '{}')
    lastReferrerCodeObj[uuid as string] = referrer_code as string
    localStorage.setItem('lastReferrerCodeObj', JSON.stringify(lastReferrerCodeObj))
    // 不要 next()，继续往下判断
  }

  const auth = useAuthStore()

  // ✨ 关键：等待认证状态就绪，避免竞态条件
  // 首次导航时此调用会立即返回（因为 hydrateFromStorage 已执行）
  // 后续导航时 ready 已为 true，无性能损耗
  await auth.whenReady()

  const publicRoute = isPublicRoute(to)

  // 已登录访问登录页 -> 优先回跳安全 redirect；否则回默认页
  if (auth.isAuthenticated && to.name === LOGIN_ROUTE.name) {
    const redirect = getSafeRedirect(to.query?.redirect)
    const target =
      redirect && redirect !== LOGIN_ROUTE.path
        ? redirect
        : DEFAULT_ROUTE.path || DEFAULT_ROUTE_PATH

    return next({ path: target, replace: true })
  }

  // 未登录访问受保护页 -> 去登录并带 redirect
  if (!auth.isAuthenticated && !publicRoute) {
    if (referrer_code) {
      return next({
        name: LOGIN_ROUTE.name,
        query: { redirect: encodeURIComponent(to.fullPath), referrer_code: referrer_code },
        replace: true,
      })
    } else {
      return next({
        name: LOGIN_ROUTE.name,
        query: { redirect: encodeURIComponent(to.fullPath) },
        replace: true,
      })
    }
  }

  // 其他情况：放行
  return next()
}

/**
 * 统一访问控制守卫（基于 meta）
 * 支持：
 *  - meta.permission             (string)      兼容旧写法
 *  - meta.permissionsAnyOf       (string[])
 *  - meta.rolesAnyOf             (string[])
 *  - meta.featureFlagsAllOf      (string[])    可选功能开关
 *  - meta.policy { action, resource }          可选策略校验
 */
export const accessGuard: NavigationGuard = async (to, from, next) => {
  // 统一采用 next 风格，避免与“返回布尔值风格”混用
  if (isPublicRoute(to)) return next()

  const auth = useAuthStore()

  // 1) 角色/权限点判断
  const roleList = (to.meta?.rolesAnyOf as string[] | undefined) ?? []
  const permList = (to.meta?.permissionsAnyOf as string[] | undefined) ?? []
  const singlePermission = to.meta?.permission as string | undefined // 兼容旧写法

  if (roleList.length && typeof (auth as any).hasRole === 'function') {
    const hasAnyRole = roleList.some((r) => (auth as any).hasRole(r))
    if (!hasAnyRole) {
      return next({
        path: '/403',
        query: { reason: 'role', roles: roleList.join(','), path: to.fullPath },
      } as RouteLocationRaw)
    }
  }

  if (permList.length) {
    const ok = permList.some((p) => auth.hasAuthority?.(p))
    if (!ok) {
      return next({
        path: '/403',
        query: { reason: 'permission', permissions: permList.join(','), path: to.fullPath },
      } as RouteLocationRaw)
    }
  } else if (singlePermission) {
    if (!auth.hasAuthority?.(singlePermission)) {
      return next({
        path: '/403',
        query: { reason: 'permission', permissions: singlePermission, path: to.fullPath },
      } as RouteLocationRaw)
    }
  }

  // 2) 功能开关（可选）
  const flags = (auth as any).flags // 约定：auth.flags.isEnabled(key) -> boolean
  const needFlags: string[] = (to.meta?.featureFlagsAllOf as string[] | undefined) ?? []
  if (needFlags.length && flags?.isEnabled) {
    const allOn = needFlags.every((k) => flags.isEnabled(k))
    if (!allOn) {
      return next({
        path: '/403',
        query: { reason: 'flag', flags: needFlags.join(','), path: to.fullPath },
      })
    }
  }

  // 3) 策略校验（ABAC/ReBAC，可选）
  const policy = to.meta?.policy as
    | {
        action: string
        resource: Record<string, any> | ((to: RouteLocationNormalized) => Record<string, any>)
      }
    | undefined

  const canFn = (auth as any).can
  if (policy && typeof canFn === 'function') {
    const resource = typeof policy.resource === 'function' ? policy.resource(to) : policy.resource
    const allowed = await canFn({ action: policy.action, resource })
    if (!allowed) {
      return next({
        path: '/403',
        query: { reason: 'policy', action: policy.action, path: to.fullPath },
      })
    }
  }

  return next()
}

/**
 * 403 Forbidden 守卫：进入 403 时做清理/注入
 */
export const ForbiddenGuard: { before: NavigationGuard } = {
  // 保留清理行为；不再写 to.params；最后必须 next()
  before(to, _from, next) {
    // 兼容 permission(s) 字段
    const permission = (to.query.permission || (to.query.permissions as string)) as
      | string
      | undefined

    if (to.name === '403' && (permission || to.query.path)) {
      const targetPath = to.query.path as string | undefined
      if (targetPath) clearPage(targetPath)
      // 403 页若需要参数，建议在 403 页面通过 route.query 读取
    }
    return next()
  },
}

// ============ 注册顺序建议 ============
// 1) 登录拦截 -> 2) 访问控制 -> 3) 403/404 等
const beforeGuards: NavigationGuard[] = [
  loginGuard,
  accessGuard,
  ForbiddenGuard.before,
  // NotFoundGuard.before, // 需要时再开启
].filter(Boolean) as NavigationGuard[]

const afterGuards: NavigationHookAfter[] = [
  // 可在这里做埋点等
].filter(Boolean) as NavigationHookAfter[]

export default {
  before: beforeGuards,
  after: afterGuards,
}
